/**
 * Sprint 4D.2 — AnagraficaDatiForm
 *
 * Componente riusabile per la sezione "Dati anagrafici" di un'anagrafica.
 * Usato in 3 punti di ingresso:
 *   1. AnagraficaDetail.jsx, tab "Dati anagrafici" (embedded=true)
 *   2. Anagrafiche.jsx, modal "Nuova anagrafica" (embedded=false)
 *   3. AnagraficaPicker.jsx, bottone "+" nuova anagrafica (embedded=false)
 *
 * Controllato: il parent gestisce `value` + `onChange`. Il componente NON
 * fa chiamate API (quelle restano sul parent) — si occupa solo del form,
 * della modale CALC e della validazione client-side.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CategorieEditor from "@/components/anagrafiche/CategorieEditor";
import ProvinciaPicker from "@/components/ProvinciaPicker";
import NazionePicker from "@/components/NazionePicker";
import ComunePicker from "@/components/ComunePicker";
import CapPicker from "@/components/CapPicker";

export const TIPO_PERSONA_LABELS = {
  privato: "Privato",
  azienda: "Azienda",
  pubblica_amministrazione: "Pubblica Amministrazione",
};

// Sprint 7: stessa regola del backend (ha_identificativo_fiscale): almeno uno dei tre identificativi
export function validateCfPivaObbligatori(a) {
  const presente = ["codice_fiscale", "partita_iva", "id_fiscale_estero"]
    .some((k) => String(a?.[k] || "").trim());
  return presente ? null
    : "Identificativo fiscale obbligatorio: codice fiscale, partita IVA o identificativo fiscale estero";
}

export default function AnagraficaDatiForm({
  value,
  onChange,
  taxonomy = {},
  mode = "edit", // "create" | "edit"
  hideCategorie = false,
  // Sprint 4D.2 (E1): warnings provenienti dal backend
  warnings = [],
  onRequestCfCheck = null, // callback per "Verifica con CALC" banner
  readOnly = false, // Sprint 4X.2: visualizzazione sola lettura
  // Sprint UTENTI-2A: rimosse props canManageAccess / accessForm /
  // setAccessForm / onResetPassword / hideAccessSection (legame anagrafica→
  // utente smontato). I caller che le passavano sono già stati aggiornati.
}) {
  const a = value || {};
  const set = (patch) => onChange?.({ ...a, ...patch });
  const setIndirizzo = (patch) =>
    onChange?.({ ...a, indirizzo: { ...(a.indirizzo || {}), ...patch } });

  const [cfHelperOpen, setCfHelperOpen] = useState(false);

  const tp = a.tipo_persona || "privato";
  const fiscaleMancante = !!validateCfPivaObbligatori(a);
  const cfRequired = fiscaleMancante;
  const pivaRequired = fiscaleMancante;

  // ---- Cluster GEO-DATA Fase C: residenza guidata Italia/Estero ----
  // Default IT se non valorizzata. `indirizzo.nazione` memorizza il code
  // (es. "IT" / "Z112"). Per record legacy con nazione vuota o stringhe
  // come "Italia"/"ITALIA", consideriamo equivalente a IT (non mutiamo il
  // valore finché l'utente non agisce esplicitamente sul picker).
  const nazioneResCode = (a.indirizzo?.nazione || "").toString().toUpperCase();
  const isResItaly =
    !nazioneResCode || nazioneResCode === "IT" || nazioneResCode === "ITALIA";
  const nazioneResValue = isResItaly ? "IT" : nazioneResCode;
  // Oggetto comune corrente (per alimentare CapPicker con i CAP del comune)
  const [comuneResObj, setComuneResObj] = useState(null);

  // ---- Cluster GEO-DATA Fase C: nascita guidata Italia/Estero (solo privato) ----
  const nazioneNascCode = (a.nazione_nascita || "IT").toString().toUpperCase();
  const isNascItaly = nazioneNascCode === "IT";
  const [comuneNascObj, setComuneNascObj] = useState(null);

  // ---- CF live (anti-loop) ----
  // cfAutoGenerated: true se il CF visualizzato è stato calcolato dal sistema
  //   (live oppure tramite CALC "calculate"). Se l'utente edita il campo CF
  //   manualmente, oppure dopo un parse (CALC inverso), questo flag diventa
  //   FALSE → il calcolo live NON sovrascrive più finché il flag resta false.
  const cfAutoRef = useRef(false);
  const lastCalcInputRef = useRef("");
  const cfDebounceRef = useRef(null);

  const requiresTelefono = useMemo(() => {
    const base = a.categorie?.base || [];
    return base.includes("cliente") || base.includes("lead");
  }, [a]);

  // Sprint UTENTI-2A: showAccessSection rimosso.

  // Live CF: ricalcola quando cambia uno dei 5 input. Solo per tipo_persona=privato.
  useEffect(() => {
    if (tp !== "privato") return;
    if (readOnly) return;
    const nome = (a.nome || "").trim();
    const cognome = (a.cognome || "").trim();
    const dataNasc = (a.data_nascita || "").trim();
    const sesso = (a.sesso || "").trim();
    // Birthplace per il CF: in Italia = nome comune, all'estero = nome Stato
    // (python-codicefiscale accetta entrambi i casi).
    let birthplace = "";
    if (isNascItaly) {
      birthplace = (a.citta_nascita || "").trim();
    } else {
      // Estero: deduco il nome dallo `_nazioneNascNome` (label dell'opzione
      // selezionata). Se non ho il nome, salto.
      birthplace = (a._nazione_nascita_nome || a.citta_nascita || "").trim();
    }

    const inputs = JSON.stringify({ nome, cognome, dataNasc, sesso, birthplace });
    if (!nome || !cognome || !dataNasc || !sesso || !birthplace) {
      lastCalcInputRef.current = inputs;
      return;
    }
    if (inputs === lastCalcInputRef.current) return;
    lastCalcInputRef.current = inputs;

    // Anti-loop: se l'utente ha un CF manuale (cfAutoRef=false) e il campo
    // NON è vuoto, NON sovrascrivere.
    const cfAttuale = (a.codice_fiscale || "").trim();
    if (cfAttuale && !cfAutoRef.current) return;

    if (cfDebounceRef.current) clearTimeout(cfDebounceRef.current);
    cfDebounceRef.current = setTimeout(async () => {
      try {
        const r = await api.post("/anagrafiche/cf-helper", {
          mode: "calculate",
          data: {
            nome, cognome,
            data_nascita: dataNasc, sesso,
            comune_nascita: birthplace,
            provincia_nascita: a.provincia_nascita || null,
          },
        });
        const newCf = (r.data?.codice_fiscale || "").toUpperCase();
        if (!newCf) return;
        // Ricontrolla anti-loop a tempo di applicazione
        const cfNow = (a.codice_fiscale || "").trim().toUpperCase();
        if (cfNow && !cfAutoRef.current) return;
        if (newCf === cfNow) return;
        cfAutoRef.current = true;
        set({ codice_fiscale: newCf });
      } catch {
        /* calcolo non riuscito: silenzioso (input parziali) */
      }
    }, 400);

    return () => { if (cfDebounceRef.current) clearTimeout(cfDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tp, a.nome, a.cognome, a.data_nascita, a.sesso,
      a.citta_nascita, a._nazione_nascita_nome, isNascItaly, readOnly]);

  // Quando l'utente edita manualmente CF → disattiva il flag auto
  const handleCfManualChange = (v) => {
    cfAutoRef.current = false;
    set({ codice_fiscale: (v || "").toUpperCase() });
  };

  return (
    <fieldset disabled={readOnly} className={readOnly ? "opacity-90 cursor-default" : ""}>
    <div className="space-y-4" data-testid="anag-dati-form">

      {/* Sprint 4D.1 (E1): banner warning CF incoerente */}
      {warnings.length > 0 && warnings.map((w, i) => (
        <Card key={i} className="p-3 bg-amber-50 border border-amber-300 flex items-center justify-between gap-3"
              data-testid={`anag-warning-${w.type}`}>
          <div className="flex items-start gap-2 text-sm">
            <span className="text-amber-700 text-lg leading-none">⚠</span>
            <div>
              <div className="font-medium text-amber-900">{w.message}</div>
              {w.differences && w.differences.length > 0 && (
                <ul className="text-xs text-amber-800 mt-1 list-disc list-inside">
                  {w.differences.map((d, j) => (
                    <li key={j}>
                      {d.field}: atteso <span className="font-mono">{d.expected}</span> · trovato <span className="font-mono">{d.got}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => (onRequestCfCheck ? onRequestCfCheck() : setCfHelperOpen(true))}
                  className="shrink-0 border-amber-400 text-amber-800 hover:bg-amber-100"
                  data-testid="anag-warning-verify-btn">
            Verifica con CALC
          </Button>
        </Card>
      ))}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Tipo persona</Label>
          <Select value={tp} onValueChange={(v) => set({ tipo_persona: v })}>
            <SelectTrigger data-testid="anag-form-tipo"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TIPO_PERSONA_LABELS).map(([k, v]) =>
                <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {tp === "privato" ? (
          <>
            <div>
              <Label className="text-xs">Nome <span className="text-red-600 font-bold">*</span></Label>
              <Input value={a.nome || ""} onChange={(e) => set({ nome: e.target.value })}
                     data-testid="anag-form-nome" />
            </div>
            <div>
              <Label className="text-xs">Cognome <span className="text-red-600 font-bold">*</span></Label>
              <Input value={a.cognome || ""} onChange={(e) => set({ cognome: e.target.value })}
                     data-testid="anag-form-cognome" />
            </div>
          </>
        ) : (
          <div className="col-span-2">
            <Label className="text-xs">Ragione sociale <span className="text-red-600 font-bold">*</span></Label>
            <Input value={a.ragione_sociale || ""}
                   onChange={(e) => set({ ragione_sociale: e.target.value })}
                   data-testid="anag-form-ragione" />
          </div>
        )}
        <div>
          <Label className="text-xs">
            P.IVA{pivaRequired && <span className="text-red-600 font-bold ml-0.5" data-testid="anag-piva-required">*</span>}
          </Label>
          <Input value={a.partita_iva || ""}
                 onChange={(e) => set({ partita_iva: e.target.value })}
                 className="font-mono"
                 data-testid="anag-form-piva" />
        </div>
        <div>
          <Label className="text-xs">
            Codice fiscale{cfRequired && <span className="text-red-600 font-bold ml-0.5" data-testid="anag-cf-required">*</span>}
          </Label>
          <div className="flex gap-1">
            <Input value={a.codice_fiscale || ""}
                   onChange={(e) => handleCfManualChange(e.target.value)}
                   className="font-mono uppercase" data-testid="anag-cf-input" />
            <Button type="button" size="sm" variant="outline"
                    onClick={() => setCfHelperOpen(true)}
                    title="Calcola, verifica o estrae il codice fiscale dai dati"
                    data-testid="anag-cf-calc-btn"
                    className="shrink-0 px-2">
              <Calculator className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs">
            Identificativo fiscale estero{fiscaleMancante && <span className="text-red-600 font-bold ml-0.5">*</span>}
          </Label>
          <Input value={a.id_fiscale_estero || ""}
                 onChange={(e) => set({ id_fiscale_estero: e.target.value })}
                 className="font-mono" data-testid="anag-form-id-fiscale-estero" />
          {fiscaleMancante && <p className="text-[11px] text-red-600 mt-0.5" data-testid="anag-identificativo-hint">Serve almeno uno fra codice fiscale, P.IVA e identificativo estero</p>}
        </div>
        <div>
          <Label className="text-xs">
            Telefono
            {requiresTelefono && <span className="text-red-600 font-bold ml-0.5" data-testid="anag-telefono-required">*</span>}
          </Label>
          <Input value={a.telefono || ""} onChange={(e) => set({ telefono: e.target.value })}
                 data-testid="anag-telefono-input" />
        </div>
        <div>
          <Label className="text-xs">
            Mobile
            {requiresTelefono && <span className="text-red-600 font-bold ml-0.5">*</span>}
          </Label>
          <Input value={a.mobile || ""} onChange={(e) => set({ mobile: e.target.value })} />
        </div>
        <div><Label className="text-xs">Email</Label>
          <Input type="email" value={a.email || ""} onChange={(e) => set({ email: e.target.value })} /></div>
        <div><Label className="text-xs">PEC</Label>
          <Input type="email" value={a.pec || ""} onChange={(e) => set({ pec: e.target.value })} /></div>
        <div><Label className="text-xs">IBAN</Label>
          <Input value={a.iban || ""} onChange={(e) => set({ iban: e.target.value })} className="font-mono" /></div>
        <div><Label className="text-xs">Banca</Label>
          <Input value={a.banca || ""} onChange={(e) => set({ banca: e.target.value })} /></div>
        <div><Label className="text-xs">Sito web</Label>
          <Input value={a.sito_web || ""} onChange={(e) => set({ sito_web: e.target.value })} /></div>
      </div>

      <div className="border-t pt-3" data-testid="anag-indirizzo-section">
        <Label className="text-xs uppercase tracking-wider text-slate-500">Indirizzo</Label>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 mt-2">
          {/* Nazione (sempre) */}
          <div className="sm:col-span-3">
            <Label className="text-[11px] text-slate-500">Nazione</Label>
            <NazionePicker
              value={nazioneResValue}
              onChange={(code, naz) => {
                // Cambio nazione: reset comune/cap/provincia se non IT.
                if (!code || code === "IT") {
                  setIndirizzo({ nazione: code || "IT" });
                } else {
                  setIndirizzo({
                    nazione: code,
                    // mantengo i campi liberi compilati dall'utente,
                    // ma li mostro come input liberi
                  });
                }
                setComuneResObj(null);
              }}
              testId="anag-indirizzo-nazione"
            />
          </div>

          {/* Via (sempre) */}
          <div className="sm:col-span-5">
            <Label className="text-[11px] text-slate-500">Via</Label>
            <Input placeholder="Via, civico…"
                   value={a.indirizzo?.via || ""}
                   onChange={(e) => setIndirizzo({ via: e.target.value })}
                   data-testid="anag-indirizzo-via" />
          </div>

          {isResItaly ? (
            <>
              <div className="sm:col-span-4">
                <Label className="text-[11px] text-slate-500">Comune</Label>
                <ComunePicker
                  value={a.indirizzo?.comune || ""}
                  provinciaFilter={a.indirizzo?.provincia || ""}
                  onChange={(nome, comune) => {
                    setComuneResObj(comune || null);
                    const patch = { comune: nome || "" };
                    // Auto-bind provincia se assente o se è coerente al cambio
                    if (comune?.provincia_sigla) {
                      patch.provincia = comune.provincia_sigla;
                    }
                    // Se il CAP corrente non è nel nuovo comune, azzera
                    if (comune && a.indirizzo?.cap
                        && !(comune.cap || []).includes(a.indirizzo.cap)) {
                      patch.cap = "";
                    }
                    setIndirizzo(patch);
                  }}
                  testId="anag-indirizzo-comune"
                />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[11px] text-slate-500">CAP</Label>
                <CapPicker
                  value={a.indirizzo?.cap || ""}
                  comune={comuneResObj}
                  onChange={(v) => setIndirizzo({ cap: v })}
                  testId="anag-indirizzo-cap"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] text-slate-500">Prov.</Label>
                <ProvinciaPicker
                  value={a.indirizzo?.provincia || ""}
                  onChange={(v) => setIndirizzo({ provincia: v || "" })}
                  testId="anag-indirizzo-provincia"
                />
              </div>
            </>
          ) : (
            <>
              <div className="sm:col-span-4">
                <Label className="text-[11px] text-slate-500">Città</Label>
                <Input value={a.indirizzo?.comune || ""}
                       onChange={(e) => setIndirizzo({ comune: e.target.value })}
                       placeholder="Città estera"
                       data-testid="anag-indirizzo-comune-estero" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] text-slate-500">CAP / ZIP</Label>
                <Input value={a.indirizzo?.cap || ""}
                       onChange={(e) => setIndirizzo({ cap: e.target.value })}
                       placeholder="ZIP"
                       className="font-mono"
                       data-testid="anag-indirizzo-cap-estero" />
              </div>
              <div className="sm:col-span-3">
                <Label className="text-[11px] text-slate-500">Stato / Regione</Label>
                <Input value={a.indirizzo?.provincia || ""}
                       onChange={(e) => setIndirizzo({ provincia: e.target.value })}
                       placeholder="Es. Baviera"
                       data-testid="anag-indirizzo-provincia-estero" />
              </div>
            </>
          )}
        </div>
      </div>

      {tp === "privato" && (
        <div className="border-t pt-3" data-testid="anag-birth-section">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Dati di nascita</Label>
          <p className="text-[11px] text-slate-400 mb-2">
            Necessari per il calcolo automatico del codice fiscale. Il CF viene
            ricalcolato live mentre digiti; il valore inserito manualmente non
            viene mai sovrascritto.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
            <div className="sm:col-span-3">
              <Label className="text-[11px] text-slate-500">Data nascita</Label>
              <Input type="date" value={a.data_nascita || ""}
                     onChange={(e) => set({ data_nascita: e.target.value })}
                     data-testid="anag-data-nascita" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-slate-500">Sesso</Label>
              <Select value={a.sesso || ""} onValueChange={(v) => set({ sesso: v })}>
                <SelectTrigger data-testid="anag-sesso"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="F">F</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3">
              <Label className="text-[11px] text-slate-500">Cittadinanza</Label>
              <Input value={a.cittadinanza || ""} onChange={(e) => set({ cittadinanza: e.target.value })}
                     data-testid="anag-cittadinanza" placeholder="Es. Italiana" />
            </div>
            <div className="sm:col-span-3">
              <Label className="text-[11px] text-slate-500">Nazione di nascita</Label>
              <NazionePicker
                value={a.nazione_nascita || "IT"}
                onChange={(code, naz) => {
                  const patch = { nazione_nascita: code || "IT" };
                  if (!code || code === "IT") {
                    // Torna in Italia: lascia comune/provincia all'utente
                    patch._nazione_nascita_nome = null;
                    // Se la provincia era "EE", azzeriamola
                    if ((a.provincia_nascita || "").toUpperCase() === "EE") {
                      patch.provincia_nascita = "";
                    }
                  } else {
                    // Estero: comune nascita = nome dello Stato (per encode CF),
                    // provincia = "EE", utente può ancora editare entrambi
                    patch._nazione_nascita_nome = naz?.nome || null;
                    patch.citta_nascita = naz?.nome || "";
                    patch.provincia_nascita = "EE";
                  }
                  set(patch);
                  setComuneNascObj(null);
                }}
                testId="anag-nazione-nascita"
              />
            </div>
            {isNascItaly ? (
              <>
                <div className="sm:col-span-2">
                  <Label className="text-[11px] text-slate-500">Comune nascita</Label>
                  <ComunePicker
                    value={a.citta_nascita || ""}
                    provinciaFilter={a.provincia_nascita || ""}
                    onChange={(nome, comune) => {
                      setComuneNascObj(comune || null);
                      const patch = { citta_nascita: nome || "" };
                      if (comune?.provincia_sigla) {
                        patch.provincia_nascita = comune.provincia_sigla;
                      }
                      set(patch);
                    }}
                    testId="anag-citta-nascita"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[11px] text-slate-500">Prov.</Label>
                  <ProvinciaPicker
                    value={a.provincia_nascita || ""}
                    onChange={(v) => set({ provincia_nascita: (v || "").toUpperCase().slice(0, 2) })}
                    testId="anag-provincia-nascita"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="sm:col-span-3">
                  <Label className="text-[11px] text-slate-500">Località estera (informativa)</Label>
                  <Input value={a.citta_nascita_estera || ""}
                         onChange={(e) => set({ citta_nascita_estera: e.target.value })}
                         placeholder="Es. Berlino"
                         data-testid="anag-citta-nascita-estera" />
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-[11px] text-slate-500">Prov.</Label>
                  <Input value={a.provincia_nascita || "EE"}
                         onChange={(e) => set({ provincia_nascita: e.target.value.toUpperCase().slice(0, 2) })}
                         maxLength={2}
                         className="font-mono"
                         data-testid="anag-provincia-nascita-estero" />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {!hideCategorie && (
        <CategorieEditor taxonomy={taxonomy}
                         value={a.categorie || {}}
                         onChange={(c) => set({ categorie: c })} />
      )}

      {/* Sprint UTENTI-2A (2026-05-31): sezione "Accesso al gestionale"
          RIMOSSA dall'anagrafica. Gli utenti si gestiscono SOLO da
          Impostazioni > Utenti. Le categorie `utenti_interni` restano come
          tag descrittivi nel selettore Categorie sopra. */}

      <div className="border-t pt-3">
        <Label className="text-xs">Note</Label>
        <Textarea value={a.note || ""} onChange={(e) => set({ note: e.target.value })} rows={3} />
      </div>

      <CfHelperDialog
        open={cfHelperOpen}
        onClose={() => setCfHelperOpen(false)}
        anagrafica={a}
        onApply={(patch) => set(patch)}
        onCfMode={(mode) => {
          // Anti-loop: dopo "calculate", il CF è generato dal sistema (auto=true)
          //   → il live può continuare a ricalcolarlo coerentemente.
          // Dopo "parse" (inverso), il CF è considerato "fonte di verità" che
          //   ha generato i dati → trattato come manuale (auto=false), così
          //   l'effetto live NON lo sovrascrive subito dopo il parse.
          if (mode === "calculate") cfAutoRef.current = true;
          else if (mode === "parse") cfAutoRef.current = false;
        }}
      />
    </div>
    </fieldset>
  );
}

// -------- Modale calcolatore CF (spostata qui, prima era in AnagraficaDetail) --------
function CfHelperDialog({ open, onClose, anagrafica, onApply, onCfMode }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  if (!anagrafica) return null;
  const cfPresente = !!(anagrafica.codice_fiscale || "").trim();
  const datiPresenti = !!(anagrafica.nome && anagrafica.cognome
                          && anagrafica.data_nascita && anagrafica.citta_nascita);
  let autoMode = "calculate";
  if (cfPresente && datiPresenti) autoMode = "verify";
  else if (cfPresente && !datiPresenti) autoMode = "parse";

  const run = async (mode) => {
    setBusy(true); setResult(null);
    try {
      const data = {
        nome: anagrafica.nome || null,
        cognome: anagrafica.cognome || null,
        data_nascita: anagrafica.data_nascita || null,
        sesso: anagrafica.sesso || null,
        comune_nascita: anagrafica.citta_nascita || null,
        provincia_nascita: anagrafica.provincia_nascita || null,
        codice_fiscale: anagrafica.codice_fiscale || null,
      };
      const r = await api.post("/anagrafiche/cf-helper", { mode, data });
      setResult({ mode, ...r.data });
      if (mode === "calculate") {
        onCfMode?.("calculate");
        onApply({ codice_fiscale: r.data.codice_fiscale });
        toast.success(`CF calcolato: ${r.data.codice_fiscale}`);
      } else if (mode === "verify") {
        if (r.data.coherent) toast.success("CF coerente con i dati anagrafici ✓");
        else toast.error("CF non coerente — verifica differenze");
      } else if (mode === "parse") {
        const patch = {};
        if (r.data.data_nascita) patch.data_nascita = r.data.data_nascita;
        if (r.data.sesso) patch.sesso = r.data.sesso;
        if (r.data.comune_nascita) patch.citta_nascita = r.data.comune_nascita;
        if (r.data.provincia_nascita) patch.provincia_nascita = r.data.provincia_nascita;
        // Marca il CF come "manuale" (protetto) PRIMA di applicare il patch,
        // così l'effetto live non rigenera/sovrascrive subito.
        onCfMode?.("parse");
        onApply(patch);
        toast.success("Dati anagrafici estratti dal CF");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cf-helper-modal">
        <DialogHeader>
          <DialogTitle>Calcolatore Codice Fiscale</DialogTitle>
          <DialogDescription>
            Modalità auto-rilevata: <span className="font-semibold">{
              autoMode === "calculate" ? "Calcola CF dai dati" :
              autoMode === "verify" ? "Verifica coerenza CF/dati" : "Estrae dati dal CF"
            }</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-slate-500">Nome:</span> {anagrafica.nome || "—"}</div>
            <div><span className="text-slate-500">Cognome:</span> {anagrafica.cognome || "—"}</div>
            <div><span className="text-slate-500">Data nascita:</span> {anagrafica.data_nascita || "—"}</div>
            <div><span className="text-slate-500">Sesso:</span> {anagrafica.sesso || "—"}</div>
            <div><span className="text-slate-500">Comune:</span> {anagrafica.citta_nascita || "—"}</div>
            <div><span className="text-slate-500">CF:</span> <span className="font-mono">{anagrafica.codice_fiscale || "—"}</span></div>
          </div>
          {result && (
            <Card className={`p-3 ${result.coherent === false ? "bg-red-50 border-red-200" :
                                     result.coherent === true ? "bg-emerald-50 border-emerald-200" :
                                     "bg-blue-50 border-blue-200"}`}>
              {result.mode === "calculate" && <div className="font-mono text-sm">CF: {result.codice_fiscale}</div>}
              {result.mode === "verify" && (
                result.coherent
                  ? <div className="text-emerald-800">✓ CF coerente con i dati</div>
                  : (
                    <>
                      <div className="text-red-800 font-medium mb-1">⚠ CF NON coerente — differenze:</div>
                      <ul className="list-disc list-inside text-xs text-red-700 space-y-0.5">
                        {result.differences.map((d, i) => <li key={i}>{d}</li>)}
                      </ul>
                      <div className="text-[11px] text-slate-600 mt-1">
                        Calcolato dai dati: <span className="font-mono">{result.calculated_cf}</span>
                      </div>
                    </>
                  )
              )}
              {result.mode === "parse" && (
                <div className="space-y-1 text-xs">
                  <div>Dati estratti dal CF (Nome/Cognome NON sono determinabili dal solo CF):</div>
                  <div>Data nascita: <span className="font-mono">{result.data_nascita}</span></div>
                  <div>Sesso: <span className="font-mono">{result.sesso}</span></div>
                  <div>Comune: <span className="font-mono">{result.comune_nascita}</span> ({result.provincia_nascita})</div>
                </div>
              )}
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button onClick={() => run(autoMode)} disabled={busy} className="bg-blue-600 hover:bg-blue-700"
                  data-testid="cf-helper-run-btn">
            {busy ? "..." : autoMode === "calculate" ? "Calcola" : autoMode === "verify" ? "Verifica" : "Estrae dati"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
